import { observable, computed, action, runInAction } from 'mobx'
import { notificationService } from 'services'

export default class NotificationStore {
  @observable notifications = []
  @observable loading = false
  @observable initialized = false

  constructor(rootStore) {
    this.rootStore = rootStore
  }

  @computed
  get unreadCount() {
    return this.notifications.filter((notification) => !notification.isRead).length
  }

  @computed
  get readCount() {
    return this.notifications.filter((notification) => notification.isRead).length
  }

  @action.bound
  setNotifications(notifications) {
    this.notifications = Array.isArray(notifications) ? notifications : []
    this.initialized = true
  }

  @action.bound
  upsert(notification) {
    if (!notification || !notification.id) return false

    const currentNotifications = Array.isArray(this.notifications) ? [...this.notifications] : []
    const index = currentNotifications.findIndex((item) => item.id === notification.id)
    const isNewNotification = index < 0

    if (index >= 0) currentNotifications[index] = { ...currentNotifications[index], ...notification }
    else currentNotifications.unshift(notification)

    currentNotifications.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))

    this.notifications = currentNotifications
    this.initialized = true
    return isNewNotification
  }

  @action.bound
  prepend(notification) {
    return this.upsert(notification)
  }

  @action.bound
  async loadNotifications(force = false) {
    if (this.loading || (this.initialized && !force)) return this.notifications

    this.loading = true
    try {
      const notifications = await notificationService.getCurrent(false)
      runInAction(() => {
        this.setNotifications(notifications)
      })
      return notifications
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound
  async markAsRead(id) {
    const notification = await notificationService.markAsRead(id)
    runInAction(() => {
      this.upsert(notification)
    })
    return notification
  }

  @action.bound
  async remove(id) {
    await notificationService.remove(id)
    runInAction(() => {
      this.notifications = this.notifications.filter((notification) => notification.id !== id)
    })
  }

  @action.bound
  async markAllAsRead(notifications = this.notifications) {
    const unreadNotifications = (notifications || []).filter((notification) => !notification.isRead)
    if (unreadNotifications.length === 0) return []

    const updatedNotifications = await notificationService.markAllAsRead(
      unreadNotifications.map((notification) => notification.id)
    )

    runInAction(() => {
      updatedNotifications.forEach((notification) => this.upsert(notification))
    })

    return updatedNotifications
  }

  @action.bound
  async removeMany(notifications = this.notifications) {
    const ids = (notifications || []).map((notification) => notification.id)
    if (ids.length === 0) return

    await notificationService.removeMany(ids)

    runInAction(() => {
      this.notifications = this.notifications.filter((notification) => !ids.includes(notification.id))
    })
  }
}
